import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Label,
} from "recharts";
import { useContextAwareGetProjectResultsByGas } from "@hooks/index";
import { ProjectGasResults } from "@features/project/projectTypes";
import {
  EmissionsByGas,
  GasType,
} from "@pages/project/results/hooks/resultTypes";
import { formatNumber } from "@utils/helperFunctions";
import GraphContainer from "./GraphContainer";
import Skeleton from "react-loading-skeleton";
import tinycolor from "tinycolor2";
import TranslatableText from "@components/translations/TranslatableText";
import CustomTooltip from "./CustomTooltip";
import {
  Tooltip as InfoTooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/tooltip/Tooltip";
import { BiQuestionMark } from "react-icons/bi";
import { useIconSize } from "@app/hooks";

interface InventoryBalanceChartProps {
  width?: string | number;
  height: string | number;
  className?: string;
  projectId: number;
  selectedActivities?: number[];
}

interface InventoryItem {
  name: string;
  value: number;
  isEmission: boolean;
  gasType?: GasType;
}

// Helper function to extract and aggregate inventory data
const extractInventoryData = (
  results: ProjectGasResults | undefined
): InventoryItem[] => {
  if (!results?.activities) return [];

  const aggregatedMap = new Map<string, number>();

  results.activities.forEach((activity) => {
    activity.modules?.forEach((module) => {
      const moduleResults = module.results;

      if (
        moduleResults &&
        !("details" in moduleResults) &&
        "total_w" in moduleResults &&
        "inventory" in moduleResults
      ) {
        const emissionsTotal = moduleResults as EmissionsByGas;
        emissionsTotal.inventory.forEach((inventory) => {
          const key = inventory.gas_type;
          // CO2 inventory values represent removals (sequestration), so we negate them
          // Other gases (CH4, N2O) represent emissions, so we keep them positive
          const adjustedValue =
            key.name === "CO2" ? -inventory.value : inventory.value;
          aggregatedMap.set(
            key.name,
            (aggregatedMap.get(key.name) || 0) + adjustedValue
          );
        });
      }
    });
  });

  // Convert aggregated map to inventory items
  const inventories: InventoryItem[] = [];
  aggregatedMap.forEach((value, gasType) => {
    inventories.push({
      name: gasType,
      value: value,
      isEmission: value > 0,
      gasType: { name: gasType } as GasType,
    });
  });

  // Sort: removals first (ascending), then emissions (descending)
  return inventories.sort((a, b) => {
    if (a.isEmission && !b.isEmission) return 1;
    if (!a.isEmission && b.isEmission) return -1;
    if (!a.isEmission && !b.isEmission) return a.value - b.value;
    return b.value - a.value;
  });
};

const InventoryBalanceChart = ({
  width,
  height,
  className,
  projectId,
  selectedActivities,
}: InventoryBalanceChartProps) => {
  const iconSize = useIconSize(20);
  const paramsComponents = [
    <span style={{ fontWeight: "bold" }} />,
  ];

  const { data, isFetching } = useContextAwareGetProjectResultsByGas(
    { id: projectId, activities: selectedActivities },
    {
      refetchOnMountOrArgChange: true,
      skip: !projectId,
    }
  );

  const inventoryData = useMemo(() => {
    return extractInventoryData(data);
  }, [data]);

  const getColor = (value: number, index: number) => {
    const baseColor = value > 0 ? "#CC1212" : "#52A021"; // Red for emissions, green for removals
    return tinycolor(baseColor)
      .darken(index * 3)
      .toRgbString();
  };

  const { emissions, removals, netBalance } = useMemo(() => {
    const emissions = inventoryData
      .filter((item) => item.isEmission)
      .reduce((sum, item) => sum + item.value, 0);
    const removals = inventoryData
      .filter((item) => !item.isEmission)
      .reduce((sum, item) => sum + item.value, 0);
    return {
      emissions,
      removals,
      netBalance: emissions + removals,
    };
  }, [inventoryData]);

  // Transform data for stacked bar chart
  const chartData = useMemo(() => {
    const emissionItems = inventoryData.filter((item) => item.isEmission);
    const removalItems = inventoryData.filter((item) => !item.isEmission);

    // Create a single data point with all categories
    const dataPoint: any = { name: "Inventory" };

    emissionItems.forEach((item, index) => {
      dataPoint[`emission_${index}`] = item.value;
    });

    removalItems.forEach((item, index) => {
      dataPoint[`removal_${index}`] = item.value;
    });

    return [dataPoint];
  }, [inventoryData]);

  return (
    <>
      <div className="d-flex align-items-end mt-4" style={{ gap: "4px" }}>
        <div
          className="w-fit"
          style={{ borderBottom: "1px solid #000", padding: "4px 0" }}
        >
          <h2 className="ff-bold fs-13 text-black">
            <TranslatableText translationKey="project.inventory" />
          </h2>
        </div>
        <InfoTooltip placement="bottom-start">
          <TooltipTrigger className="d-flex" style={{ background: "transparent", border: "none", padding: 0 }}>
            <BiQuestionMark size={iconSize} color="#50A2A7" />
          </TooltipTrigger>
          <TooltipContent>
            <div className="map-guide-tooltip-content" style={{ maxWidth: 500 }}>
              <TranslatableText
                translationKey="project.inventory_tooltip"
                paramsComponents={paramsComponents}
              />
            </div>
          </TooltipContent>
        </InfoTooltip>
      </div>
      <div className="fs-11 text-black mt-2 mb-2">
        <span>
          <strong>
            <TranslatableText translationKey="project.emissions" />:
          </strong>{" "}
          {formatNumber(emissions)} tCO₂eq/yr
        </span>
        {" | "}
        <span>
          <strong>
            <TranslatableText translationKey="project.removals" />:
          </strong>{" "}
          {formatNumber(removals)} tCO₂eq/yr
        </span>
        {" | "}
        <span>
          <strong>
            <TranslatableText translationKey="project.net" />:
          </strong>{" "}
          <span
            style={{
              color: netBalance > 0 ? "#CC1212" : "#52A021",
              fontWeight: "bold",
            }}
          >
            {formatNumber(netBalance)} tCO₂eq/yr
          </span>
        </span>
      </div>
      <div className="d-flex justify-content-center gap-8 fs-11 mb-2">
        <span style={{ color: "#52A021", fontWeight: "bold" }}>← Removals</span>
        <span style={{ color: "#CC1212", fontWeight: "bold" }}>
          Emissions →
        </span>
      </div>
      <GraphContainer height={height} zIndex={10}>
        {isFetching ? (
          <Skeleton width={width ?? "97%"} height={height} />
        ) : inventoryData.length === 0 ? (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ height }}
          >
            <span className="text-gray fs-13">No inventory data available</span>
          </div>
        ) : (
          <ResponsiveContainer
            width={width ?? "97%"}
            height={height}
            className={className}
          >
            <BarChart
              data={chartData}
              layout="vertical"
              stackOffset="sign"
              margin={{
                top: 20,
                right: 25,
                left: 15,
                bottom: 5,
              }}
              maxBarSize={60}
            >
              <XAxis
                type="number"
                style={{ fontSize: "12px" }}
                tickFormatter={(value: number) => formatNumber(value)}
              />
              <YAxis
                dataKey="name"
                type="category"
                width={80}
                style={{ fontSize: "11px" }}
              />
              <Tooltip
                content={
                  <CustomTooltip
                    customFormatter={(value: number, name: string) => {
                      // Extract the original item name
                      const index = parseInt(name.split("_")[1]);
                      const isEmission = name.startsWith("emission");
                      const items = inventoryData.filter(
                        (item) => item.isEmission === isEmission
                      );
                      const item = items[index];
                      return {
                        name: item?.name || name,
                        value: formatNumber(value),
                      };
                    }}
                  />
                }
              />

              {/* Render emission bars */}
              {inventoryData
                .filter((item) => item.isEmission)
                .map((item, index) => (
                  <Bar
                    key={`emission_${index}`}
                    dataKey={`emission_${index}`}
                    stackId="stack"
                    fill={getColor(item.value, index)}
                    name={item.name}
                  />
                ))}

              {/* Render removal bars */}
              {inventoryData
                .filter((item) => !item.isEmission)
                .map((item, index) => (
                  <Bar
                    key={`removal_${index}`}
                    dataKey={`removal_${index}`}
                    stackId="stack"
                    fill={getColor(item.value, index)}
                    name={item.name}
                  />
                ))}

              <ReferenceLine x={0} stroke="#000" strokeWidth={2}>
                <Label
                  value="Neutral (0)"
                  position="top"
                  style={{ fontSize: "8px" }}
                />
              </ReferenceLine>
            </BarChart>
          </ResponsiveContainer>
        )}
      </GraphContainer>
    </>
  );
};

export default InventoryBalanceChart;
